/* Iter122dx · SuperAdmin · Gestione richieste licenze condomìni (approva / sospendi / riattiva). */
import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Building2, CheckCircle2, PauseCircle, PlayCircle, KeyRound, RefreshCcw, Filter } from "lucide-react";

const STATO_MAP = {
  richiesta: { label: "Attivazione richiesta", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  attiva:    { label: "Licenza attiva", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  sospesa:   { label: "Sospesa", cls: "bg-slate-200 text-slate-600 border-slate-300" },
};

export default function SuperAdminLicenzeCondomini() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("richiesta");
  const [busy, setBusy] = useState(null); // cid
  const load = async () => {
    try { const { data } = await api.get("/superadmin/licenze-cond-richieste"); setItems(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore caricamento"); }
  };
  useEffect(() => { load(); }, []);
  const filtered = filter === "all" ? items : items.filter((i) => i.licenza_stato === filter);

  const doAction = async (cid, action, label) => {
    if (!window.confirm(`Confermi ${label}?`)) return;
    setBusy(cid);
    try {
      await api.post(`/superadmin/condomini/${cid}/${action}`);
      toast.success("OK");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Operazione non riuscita"); }
    setBusy(null);
  };

  return (
    <div className="space-y-3" data-testid="superadmin-licenze-cond">
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="font-display text-lg font-bold text-brand inline-flex items-center gap-2">
          <KeyRound className="w-5 h-5" /> Richieste licenze condomìni
        </h3>
        <Button size="sm" variant="outline" onClick={load} className="h-8 ml-auto"><RefreshCcw className="w-3.5 h-3.5 mr-1" /> Aggiorna</Button>
        <div className="w-full flex items-center gap-2 mt-1">
          <Filter className="w-4 h-4 text-slate-400" />
          {[
            ["richiesta", "Pendenti"],
            ["attiva", "Attive"],
            ["sospesa", "Sospese"],
            ["all", "Tutte"],
          ].map(([k, l]) => (
            <button key={k} data-testid={`filter-${k}`}
              onClick={() => setFilter(k)}
              className={`px-2.5 py-1 text-xs font-semibold rounded-full ${filter === k ? "bg-brand text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>{l}</button>
          ))}
        </div>
      </div>
      {filtered.length === 0 && <p className="text-sm text-slate-500 italic">Nessuna licenza {filter === "all" ? "" : `in stato "${filter}"`}.</p>}
      {filtered.map((c) => {
        const st = STATO_MAP[c.licenza_stato] || { label: c.licenza_stato, cls: "bg-slate-100" };
        return (
          <Card key={c.id} className="p-3 border-2" data-testid={`sa-lic-${c.id}`}>
            <div className="flex items-start flex-wrap gap-3">
              <Building2 className="w-5 h-5 text-brand shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <div className="font-bold">{c.nome} <span className="text-xs font-normal text-slate-500">· {c.tenant_nome}</span></div>
                <div className="text-xs text-slate-500 truncate">{c.indirizzo}, {c.citta}</div>
                <div className="text-xs text-slate-600 mt-1">
                  Piano: <b>{(c.licenza_piano || "—").toUpperCase()}</b> · Intestatario: <b>{c.licenza_intestatario || "—"}</b> · Pagamento: <b>{c.licenza_metodo_pagamento || "—"}</b>
                  {c.licenza_richiesta_at && <> · Richiesta: {new Date(c.licenza_richiesta_at).toLocaleString("it-IT")}</>}
                  {c.licenza_scadenza && <> · Scad: {new Date(c.licenza_scadenza).toLocaleDateString("it-IT")}</>}
                </div>
              </div>
              <span className={`px-2 py-0.5 rounded-full border text-xs font-bold ${st.cls}`}>{st.label}</span>
              <div className="flex gap-1">
                {c.licenza_stato === "richiesta" && (
                  <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700" disabled={busy === c.id}
                    onClick={() => doAction(c.id, "attiva", "l'attivazione (pagamento ricevuto)")}
                    data-testid={`sa-attiva-${c.id}`}>
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Conferma & Attiva
                  </Button>
                )}
                {c.licenza_stato === "attiva" && (
                  <Button size="sm" variant="outline" className="h-8 border-amber-300 text-amber-700" disabled={busy === c.id}
                    onClick={() => doAction(c.id, "sospendi", "la sospensione")}
                    data-testid={`sa-sospendi-${c.id}`}>
                    <PauseCircle className="w-4 h-4 mr-1" /> Sospendi
                  </Button>
                )}
                {["attiva", "sospesa"].includes(c.licenza_stato) && (
                  <Button size="sm" variant="outline" className="h-8 border-brand text-brand" disabled={busy === c.id}
                    onClick={() => doAction(c.id, "rinnova", "il rinnovo annuale (+365gg dalla scadenza)")}
                    data-testid={`sa-rinnova-${c.id}`}>
                    <RefreshCcw className="w-4 h-4 mr-1" /> Rinnova
                  </Button>
                )}
                {c.licenza_stato === "sospesa" && (
                  <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700" disabled={busy === c.id}
                    onClick={() => doAction(c.id, "riattiva", "la riattivazione")}
                    data-testid={`sa-riattiva-${c.id}`}>
                    <PlayCircle className="w-4 h-4 mr-1" /> Riattiva
                  </Button>
                )}
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
